import React, { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { startOfWeek, endOfWeek, format, addWeeks, subWeeks, parseISO } from 'date-fns';
import { slotsAPI, bookingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import AttendeesList from '../components/AttendeesList';
import DaySelector from '../components/DaySelector';
import type { TrainingSlot } from '../types';

const Timetable: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  
  // Initialize selectedDayIndex based on today if in current week
  const initialWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const today = new Date();
  const todayWeekStart = startOfWeek(today, { weekStartsOn: 1 });
  const isCurrentWeek = format(todayWeekStart, 'yyyy-MM-dd') === format(initialWeekStart, 'yyyy-MM-dd');
  const getInitialDayIndex = () => {
    if (isCurrentWeek) {
      const dayOfWeek = today.getDay();
      return dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    }
    return 0;
  };
  
  const [selectedDayIndex, setSelectedDayIndex] = useState(getInitialDayIndex);
  const queryClient = useQueryClient();
  const previousWeekStartRef = useRef<string>('');

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekStartKey = format(weekStart, 'yyyy-MM-dd');
  
  // Initialize the ref on first render
  if (previousWeekStartRef.current === '') {
    previousWeekStartRef.current = weekStartKey;
  }

  const { data: slots = [], isLoading } = useQuery({
    queryKey: ['slots', weekStartKey],
    queryFn: () => slotsAPI.getSlots(weekStartKey),
  });

  const { data: myBookings = [] } = useQuery({
    queryKey: ['myBookings'],
    queryFn: bookingsAPI.getMyBookings,
    enabled: isAuthenticated,
  });

  const bookingMutation = useMutation({
    mutationFn: ({ slotId, bookedDate }: { slotId: string; bookedDate: string }) =>
      bookingsAPI.createBooking(slotId, bookedDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      queryClient.invalidateQueries({ queryKey: ['myBookings'] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: bookingsAPI.deleteBooking,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      queryClient.invalidateQueries({ queryKey: ['myBookings'] });
    },
  });

  const handleBook = async (slot: TrainingSlot) => {
    if (!isAuthenticated) {
      alert('Please login to book a slot');
      return;
    }
    try {
      await bookingMutation.mutateAsync({
        slotId: (slot as any).originalId || slot.id,
        bookedDate: slot.date,
      });
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to book slot');
    }
  };

  const handleCancel = async (bookingId: string) => {
    try {
      await cancelMutation.mutateAsync(bookingId);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to cancel booking');
    }
  };

  const isBooked = (slot: TrainingSlot) => {
    const slotDate = new Date(slot.date).toDateString();
    return myBookings.some((b) => {
      const bookingDate = new Date((b as any).bookedDate || b.trainingSlot.date).toDateString();
      const bookingSlotId = (b as any).originalId || b.trainingSlotId;
      const currentSlotId = (slot as any).originalId || slot.id;
      return bookingSlotId === currentSlotId && bookingDate === slotDate;
    });
  };

  const getBookingId = (slot: TrainingSlot) => {
    const slotDate = new Date(slot.date).toDateString();
    const booking = myBookings.find((b) => {
      const bookingDate = new Date((b as any).bookedDate || b.trainingSlot.date).toDateString();
      const bookingSlotId = (b as any).originalId || b.trainingSlotId;
      const currentSlotId = (slot as any).originalId || slot.id;
      return bookingSlotId === currentSlotId && bookingDate === slotDate;
    });
    return booking?.id;
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'SWIMMING':
        return 'bg-blue-100 border-blue-300 text-blue-800';
      case 'BIKING':
        return 'bg-green-100 border-green-300 text-green-800';
      case 'RUNNING':
        return 'bg-orange-100 border-orange-300 text-orange-800';
      case 'GYM':
        return 'bg-purple-100 border-purple-300 text-purple-800';
      default:
        return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const dayDates = useMemo(() => {
    return days.map((_, index) => {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + index);
      return date;
    });
  }, [weekStart]);

  const slotsByDay = useMemo(() => {
    return days.reduce((acc, day, index) => {
      const dayDate = dayDates[index];
      acc[day] = slots.filter((slot) => {
        const slotDate = parseISO(slot.date);
        return format(slotDate, 'yyyy-MM-dd') === format(dayDate, 'yyyy-MM-dd');
      });
      return acc;
    }, {} as Record<string, TrainingSlot[]>);
  }, [slots, dayDates, days]);

  // Reset selected day ONLY when week actually changes (not on every render or day selection)
  React.useEffect(() => {
    // Only reset if the week has actually changed (not just a re-render)
    if (previousWeekStartRef.current !== weekStartKey) {
      previousWeekStartRef.current = weekStartKey;
      
      const today = new Date();
      const todayWeekStart = startOfWeek(today, { weekStartsOn: 1 });
      const isCurrentWeek = format(todayWeekStart, 'yyyy-MM-dd') === weekStartKey;
      
      if (isCurrentWeek) {
        // Find today's index in the week
        const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Monday = 0
        setSelectedDayIndex(adjustedDay);
      } else {
        setSelectedDayIndex(0);
      }
    }
    // Only run when weekStartKey changes, not on every render
  }, [weekStartKey]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">Loading timetable...</div>
      </div>
    );
  }

  const handleWeekChange = (direction: 'prev' | 'next' | 'today') => {
    if (direction === 'prev') {
      setCurrentWeek(subWeeks(currentWeek, 1));
    } else if (direction === 'next') {
      setCurrentWeek(addWeeks(currentWeek, 1));
    } else {
      const today = new Date();
      setCurrentWeek(today);
      // Set selected day to today when clicking "Today" button
      const todayWeekStart = startOfWeek(today, { weekStartsOn: 1 });
      const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Monday = 0
      setSelectedDayIndex(adjustedDay);
    }
  };

  const renderSlotCard = (slot: TrainingSlot) => {
    const booked = isBooked(slot);
    const bookingId = getBookingId(slot);
    
    return (
      <div
        key={slot.id}
        className={`border rounded-lg p-3 text-sm transition-shadow duration-200 hover:shadow-md ${getTypeColor(slot.type)}`}
      >
        <div className="font-semibold text-base leading-tight mb-1.5">{slot.title}</div>
        <div className="text-xs mt-1.5 font-medium opacity-90">
          {slot.startTime} - {slot.endTime}
        </div>
        <div className="text-xs mt-1.5 font-medium">
          {slot.bookingCount}/{slot.capacity} booked
        </div>
        <AttendeesList attendees={slot.attendees} className="mt-2" />
        {isAuthenticated && (
          <button
            onClick={() =>
              booked && bookingId
                ? handleCancel(bookingId)
                : handleBook(slot)
            }
            disabled={!booked && slot.isFull}
            className={`
              mt-3 w-full text-sm px-4 py-2.5 rounded-lg font-semibold
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-offset-2
              min-h-[44px] touch-manipulation
              ${
                booked
                  ? 'bg-accent-500 text-white hover:bg-accent-600 focus:ring-accent-500 active:scale-95'
                  : slot.isFull
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500 active:scale-95'
              }
            `}
          >
            {booked ? 'Cancel Booking' : slot.isFull ? 'Full' : 'Book Now'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-4 sm:py-6 lg:py-8 max-w-7xl">
      {/* Header Section */}
      <div className="mb-4 sm:mb-6">
        {/* Desktop Layout: Title on left, Navigation on right */}
        <div className="hidden md:flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-gray-900">
            Training Timetable
          </h1>
          
          {/* Week Navigation - Desktop */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleWeekChange('prev')}
              className="px-4 py-2.5 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium text-sm min-h-[44px] touch-manipulation"
              aria-label="Previous week"
            >
              ← Previous
            </button>
            <span className="px-4 py-2.5 text-sm font-medium text-gray-700 min-h-[44px] flex items-center">
              {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
            </span>
            <button
              onClick={() => handleWeekChange('next')}
              className="px-4 py-2.5 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium text-sm min-h-[44px] touch-manipulation"
              aria-label="Next week"
            >
              Next →
            </button>
            <button
              onClick={() => handleWeekChange('today')}
              className="px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium text-sm min-h-[44px] touch-manipulation"
            >
              Today
            </button>
          </div>
        </div>

        {/* Mobile Layout: Stacked */}
        <div className="md:hidden">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Training Timetable
          </h1>
          
          {/* Week Navigation - Mobile */}
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => handleWeekChange('prev')}
              className="flex-1 px-3 py-2.5 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium text-xs min-h-[44px] touch-manipulation"
              aria-label="Previous week"
            >
              ← Prev
            </button>
            <button
              onClick={() => handleWeekChange('today')}
              className="px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium text-xs min-h-[44px] touch-manipulation"
            >
              Today
            </button>
            <button
              onClick={() => handleWeekChange('next')}
              className="flex-1 px-3 py-2.5 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium text-xs min-h-[44px] touch-manipulation"
              aria-label="Next week"
            >
              Next →
            </button>
          </div>
          
          {/* Week Display - Mobile */}
          <div className="text-center">
            <div className="text-sm font-medium text-gray-700">
              {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
            </div>
          </div>
        </div>
      </div>

      {/* Day Selector - Mobile Only */}
      <div className="md:hidden mb-4">
        <DaySelector
          days={dayDates}
          selectedIndex={selectedDayIndex}
          onSelect={(index) => {
            // Ensure the index is valid
            if (index >= 0 && index < dayDates.length) {
              setSelectedDayIndex(index);
            }
          }}
        />
      </div>

      {/* Timetable Grid */}
      <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
        {/* Mobile: Single Day View */}
        <div className="md:hidden">
          {(() => {
            // Get the selected day directly instead of mapping
            const selectedDay = dayDates[selectedDayIndex];
            if (!selectedDay) return null;
            
            const dayName = days[selectedDayIndex];
            const slots = slotsByDay[dayName] || [];
            const selectedDateKey = format(selectedDay, 'yyyy-MM-dd');
            
            return (
              <div key={selectedDateKey} className="space-y-3">
                <div className="sticky top-0 z-10 bg-white border-b-2 border-primary-500 pb-2 mb-3">
                  <div className="font-bold text-lg text-gray-900">
                    {dayName}
                  </div>
                  <div className="text-sm text-gray-500">
                    {format(selectedDay, 'MMMM d, yyyy')}
                  </div>
                </div>
                {slots.length > 0 ? (
                  <div className="space-y-3 pb-4">
                    {slots.map(renderSlotCard)}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <p className="text-base">No sessions scheduled</p>
                    <p className="text-sm mt-1">for this day</p>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Tablet: 2-3 Column Grid */}
        <div className="hidden md:grid lg:hidden grid-cols-2 xl:grid-cols-3 gap-4">
          {days.map((day, index) => {
            const slots = slotsByDay[day] || [];
            return (
              <div key={day} className="border rounded-lg p-3 bg-white">
                <div className="sticky top-0 bg-white z-10 pb-2 mb-3 border-b border-gray-200">
                  <div className="font-semibold text-center text-sm">
                    {day}
                  </div>
                  <div className="text-xs text-gray-500 text-center font-normal mt-0.5">
                    {format(dayDates[index], 'MMM d')}
                  </div>
                </div>
                <div className="space-y-3">
                  {slots.length > 0 ? (
                    slots.map(renderSlotCard)
                  ) : (
                    <div className="text-xs text-gray-400 text-center py-6">
                      No sessions
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop: Full 7-Column Grid */}
        <div className="hidden lg:grid grid-cols-7 gap-3 xl:gap-4">
          {days.map((day, index) => {
            const slots = slotsByDay[day] || [];
            return (
              <div key={day} className="border rounded-lg p-2 xl:p-3 bg-white">
                <div className="sticky top-0 bg-white z-10 pb-2 mb-2 border-b border-gray-200">
                  <div className="font-semibold text-center text-sm xl:text-base">
                    {day}
                  </div>
                  <div className="text-xs text-gray-500 text-center font-normal mt-0.5">
                    {format(dayDates[index], 'MMM d')}
                  </div>
                </div>
                <div className="space-y-2 xl:space-y-3">
                  {slots.length > 0 ? (
                    slots.map(renderSlotCard)
                  ) : (
                    <div className="text-xs text-gray-400 text-center py-4">
                      No sessions
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Timetable;
